import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app';
import { feApiEndpoint } from 'awServerModule/runtime/util/consts';
import { parseGuestCheckoutStringToArray } from '~~/common/utils/guestCheckout.js';

function createNormalizedConfig () {
  const runtimeConfig = useRuntimeConfig();
  return {
    ...runtimeConfig,
    public: {
      ...runtimeConfig.public,
      enabledGuestCheckouts: parseGuestCheckoutStringToArray(runtimeConfig.public.enabledGuestCheckouts),
    },
  };
}

type Instance = 'Shop' | 'Dealer';

function getInstanceConfig<T extends Record<string, unknown>> (
  obj: T,
  prefix: string,
  instance: Instance,
  suffix: string = '',
): string {
  const key = `${prefix}${instance}${suffix}` as keyof T;
  return obj[key] as string;
}

function generateAwRuntimeConfig () {
  const instance: Instance = __AW_IS_SHOP__ ? 'Shop' : 'Dealer';
  const config = createNormalizedConfig();

  return {
    apiHost: import.meta.server
      ? getInstanceConfig(config, 'auchanApiHostPrivate', instance)
      : getInstanceConfig(config.public, 'auchanApiHostPublic', instance),
    auchanApiBrowserHost: getInstanceConfig(config.public, 'auchanApiBrowserHost', instance),
    apiNetworkBaseURL: (import.meta.server
      ? getInstanceConfig(config, 'auchanApiHostPrivate', instance)
      : getInstanceConfig(config.public, 'auchanApiBrowserHost', instance)) + config.public.auchanApiPrefix,
    feApiNetworkBaseURL: `${getInstanceConfig(config.public, 'auchanApiBrowserHost', instance)}${feApiEndpoint}`,
    messageTokenURL: `${(import.meta.server
      ? getInstanceConfig(config, 'auchanMessageTokenHostPrivate', instance)
      : getInstanceConfig(config.public, 'auchanMessageTokenHostPublic', instance))}${config.public.auchanMessageTokenPrefix}`,
    authMode: config.public.auchanCustomerDiamondEnabled ? 'CD' : 'local',
    mapAreaBounds: [
      {
        latitude: config.public.mapX1,
        longitude: config.public.mapY1,
      },
      {
        latitude: config.public.mapX2,
        longitude: config.public.mapY2,
      },
    ],
    customerDiamondLoginUrl: `${config.public.auchanCustomerDiamondHost}${config.public.auchanCustomerDiamondPathLogin}?client_id=${config.public.auchanCustomerDiamondClientId}&redirect_uri=${config.public.auchanCustomerDiamondRedirectUri}`,
    customerDiamondRegisterUrl: `${config.public.auchanCustomerDiamondHost}${config.public.auchanCustomerDiamondPathRegister}?client_id=${config.public.auchanCustomerDiamondClientId}&redirect_uri=${config.public.auchanCustomerDiamondRedirectUri}`,
    customerDiamondLogoutUrl: `${config.public.auchanCustomerDiamondHost}${config.public.auchanCustomerDiamondPathLogout}`,
    customerDiamondAccountDeleteUrl: config.public.auchanCustomerDiamondPathAccountDelete,
    searchMinLength: getInstanceConfig(config.public, 'searchMinLength', instance),
    searchLogType: getInstanceConfig(config.public, 'searchLogType', instance),
    barcodeScannerDetectionOrder: config.public.barcodeScannerDetectionOrder.split(/\s/),
    cetelemCalculatorUrl: `${config.public.cetelemBaseUrl}${config.public.cetelemCalculatorPath}`,
    cetelemScriptTagUrl: `${config.public.cetelemBaseUrl}${config.public.cetelemScriptTagPath}`,
    ...(instance === 'Dealer' ? {
      adminHost: import.meta.server
        ? getInstanceConfig(config, 'auchanAdminHostPrivate', instance)
        : getInstanceConfig(config.public, 'auchanAdminHostPublic', instance),
    } : {}),

  };
}

function generateMergedRuntimeConfig () {
  const config = createNormalizedConfig();
  return {
    ...config.public,
    ...(import.meta.server
      ? {
        ...config,
        public: undefined,
      }
      : null
    ),
  };
}

type DealerSpecificConfig = {
  auchanAdminHostPublicDealer?: string;
  auchanAdminPrefixDealer?: string;
  auchanAdminHostPrivateDealer?: string;
};

export type AwMergedConfigType = ReturnType<typeof generateMergedRuntimeConfig> & DealerSpecificConfig;
export type AwRuntimeConfigType = ReturnType<typeof generateAwRuntimeConfig>;

export type AwRuntimeConfigPluginProvides = {
  awMergedConfig: AwMergedConfigType;
  awRuntimeConfig: AwRuntimeConfigType;
};

function createPluginProvides (): AwRuntimeConfigPluginProvides {
  return {
    awMergedConfig: generateMergedRuntimeConfig(),
    awRuntimeConfig: generateAwRuntimeConfig(),
  };
}

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});
